import { c as _c } from "react/compiler-runtime";
import Pause from "@mui/icons-material/Pause";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Replay from "@mui/icons-material/Replay";
import SkipNext from "@mui/icons-material/SkipNext";
import SkipPrevious from "@mui/icons-material/SkipPrevious";
import { Alert, alpha, Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Provider } from "react-redux";
import { callstackSlice, selectCallstack, selectCallstackFrameIndex, selectCallstackIsPlaying, selectCallstackIsReady, selectCallstackLength, selectIsLastFrame, selectIsRootFrame } from "#/features/callstack/model/callstackSlice";
import { CompactCallstackList } from "#/features/callstack/ui/CompactCallstackList";
import { useLandingHeroPreviewLayoutTransitions } from "#/features/homePage/hooks/useLandingHeroPreviewLayoutTransitions";
import { useLandingHeroPreviewPlaybackGate } from "#/features/homePage/hooks/useLandingHeroPreviewPlaybackGate";
import { createLandingHeroPreviewStore, type LandingHeroPreviewStore } from "#/features/homePage/model/landingHeroPreviewStore";
import { usePlayerControls } from "#/features/treeViewer/hooks";
import { getPlaybackStepGroups, getPlaybackStepIndex } from "#/features/treeViewer/lib";
import { TreeViewer } from "#/features/treeViewer/ui/TreeViewer";
import type { TranslationFunctions } from "#/i18n/i18n-types";
import { useAppDispatch, useAppSelector } from "#/store/hooks";
type HomeLandingHeroPreviewRuntimeProps = {
  LL: TranslationFunctions;
  pageScrollViewport: HTMLDivElement | null;
};
const PLAYBACK_INTERVAL_MS = 600;
const REPLAY_RESET_PAUSE_MS = 220;
type LandingHeroPreviewRuntimeState = {
  store: LandingHeroPreviewStore;
  errorMessage: null;
} | {
  store: null;
  errorMessage: string;
};
const getPreviewRuntimeErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "";
};
const HomeLandingHeroPreviewRuntimeInner: React.FC<HomeLandingHeroPreviewRuntimeProps> = t0 => {
  const $ = _c(166);
  const {
    LL,
    pageScrollViewport
  } = t0;
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const previewRootRef = useRef(null);
  const hasAutoStartedRef = useRef(false);
  const hasManualOverrideRef = useRef(false);
  const wasPlayingBeforeSuppressRef = useRef(false);
  const replayTimeoutRef = useRef(null);
  const replayRestartTimeoutRef = useRef(null);
  let t1;
  if ($[0] !== pageScrollViewport) {
    t1 = {
      previewRootRef,
      pageScrollViewport
    };
    $[0] = pageScrollViewport;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isPlaybackSuppressed
  } = useLandingHeroPreviewPlaybackGate(t1);
  useLandingHeroPreviewLayoutTransitions();
  const {
    handleReset,
    handlePlay,
    handleReplay,
    handleStepBack,
    handleStepForward
  } = usePlayerControls();
  const isReady = useAppSelector(selectCallstackIsReady);
  const callstack = useAppSelector(selectCallstack);
  const isPlaying = useAppSelector(selectCallstackIsPlaying);
  const frameIndex = useAppSelector(selectCallstackFrameIndex);
  const callstackLength = useAppSelector(selectCallstackLength);
  const isLastFrame = useAppSelector(selectIsLastFrame);
  const isRootFrame = useAppSelector(selectIsRootFrame);
  let t2;
  if ($[2] !== callstack.frames) {
    t2 = getPlaybackStepGroups(callstack.frames, {
      forCallstackDisplay: true
    });
    $[2] = callstack.frames;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const playbackStepGroups = t2;
  const activePlaybackStepIndex = getPlaybackStepIndex(playbackStepGroups, frameIndex);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (replayTimeoutRef.current !== null) {
        clearTimeout(replayTimeoutRef.current);
        replayTimeoutRef.current = null;
      }
      if (replayRestartTimeoutRef.current !== null) {
        clearTimeout(replayRestartTimeoutRef.current);
        replayRestartTimeoutRef.current = null;
      }
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const clearReplayTimers = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (replayTimeoutRef.current !== null) {
        clearTimeout(replayTimeoutRef.current);
        replayTimeoutRef.current = null;
      }
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const clearAutoReplayEndDelayOnly = t4;
  let t5;
  if ($[6] !== dispatch) {
    t5 = () => {
      clearReplayTimers();
      hasManualOverrideRef.current = true;
      dispatch(callstackSlice.actions.setIsPlaying(false));
    };
    $[6] = dispatch;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const stopAutoplay = t5;
  let t6;
  if ($[8] !== handleStepBack || $[9] !== stopAutoplay) {
    t6 = () => {
      stopAutoplay();
      handleStepBack();
    };
    $[8] = handleStepBack;
    $[9] = stopAutoplay;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleManualStepBack = t6;
  let t7;
  if ($[11] !== handlePlay) {
    t7 = () => {
      hasManualOverrideRef.current = true;
      handlePlay();
    };
    $[11] = handlePlay;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleManualPlayToggle = t7;
  let t8;
  if ($[13] !== handleReplay || $[14] !== stopAutoplay) {
    t8 = () => {
      stopAutoplay();
      handleReplay();
    };
    $[13] = handleReplay;
    $[14] = stopAutoplay;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleManualReplay = t8;
  let t9;
  if ($[16] !== handleStepForward || $[17] !== stopAutoplay) {
    t9 = () => {
      stopAutoplay();
      handleStepForward();
    };
    $[16] = handleStepForward;
    $[17] = stopAutoplay;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleManualStepForward = t9;
  let t10;
  let t11;
  if ($[19] !== dispatch || $[20] !== isLastFrame || $[21] !== isPlaybackSuppressed || $[22] !== isPlaying || $[23] !== isReady) {
    t10 = () => {
      if (!isPlaybackSuppressed) {
        if (wasPlayingBeforeSuppressRef.current && !hasManualOverrideRef.current && isReady && !isLastFrame) {
          dispatch(callstackSlice.actions.setIsPlaying(true));
        }
        wasPlayingBeforeSuppressRef.current = false;
        return;
      }
      if (isPlaying) {
        wasPlayingBeforeSuppressRef.current = true;
        dispatch(callstackSlice.actions.setIsPlaying(false));
      }
      clearReplayTimers();
    };
    t11 = [clearReplayTimers, dispatch, isLastFrame, isPlaybackSuppressed, isPlaying, isReady];
    $[19] = dispatch;
    $[20] = isLastFrame;
    $[21] = isPlaybackSuppressed;
    $[22] = isPlaying;
    $[23] = isReady;
    $[24] = t10;
    $[25] = t11;
  } else {
    t10 = $[24];
    t11 = $[25];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[26] !== callstackLength || $[27] !== handlePlay || $[28] !== isPlaybackSuppressed || $[29] !== isReady) {
    t12 = () => {
      if (!isReady || callstackLength === 0 || hasAutoStartedRef.current || hasManualOverrideRef.current || isPlaybackSuppressed) {
        return;
      }
      hasAutoStartedRef.current = true;
      handlePlay();
    };
    t13 = [callstackLength, handlePlay, isPlaybackSuppressed, isReady];
    $[26] = callstackLength;
    $[27] = handlePlay;
    $[28] = isPlaybackSuppressed;
    $[29] = isReady;
    $[30] = t12;
    $[31] = t13;
  } else {
    t12 = $[30];
    t13 = $[31];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[32] !== dispatch || $[33] !== handleReset || $[34] !== isLastFrame || $[35] !== isPlaybackSuppressed || $[36] !== isPlaying || $[37] !== isReady) {
    t14 = () => {
      if (!isReady || !hasAutoStartedRef.current || hasManualOverrideRef.current || isPlaybackSuppressed || isPlaying || !isLastFrame) {
        return;
      }
      replayTimeoutRef.current = setTimeout(() => {
        replayTimeoutRef.current = null;
        handleReset();
        replayRestartTimeoutRef.current = setTimeout(() => {
          replayRestartTimeoutRef.current = null;
          if (!hasManualOverrideRef.current) {
            dispatch(callstackSlice.actions.setIsPlaying(true));
          }
        }, REPLAY_RESET_PAUSE_MS);
      }, 900);
      return clearAutoReplayEndDelayOnly;
    };
    t15 = [clearAutoReplayEndDelayOnly, dispatch, handleReset, isLastFrame, isPlaybackSuppressed, isPlaying, isReady];
    $[32] = dispatch;
    $[33] = handleReset;
    $[34] = isLastFrame;
    $[35] = isPlaybackSuppressed;
    $[36] = isPlaying;
    $[37] = isReady;
    $[38] = t14;
    $[39] = t15;
  } else {
    t14 = $[38];
    t15 = $[39];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => () => {
      clearReplayTimers();
    };
    t17 = [clearReplayTimers];
    $[40] = t16;
    $[41] = t17;
  } else {
    t16 = $[40];
    t17 = $[41];
  }
  useEffect(t16, t17);
  let t18;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      minWidth: 0
    };
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      justifyContent: "space-between",
      alignItems: "center",
      minWidth: 0
    };
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      color: "text.secondary"
    };
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== LL) {
    t21 = LL.HOME_PILLAR_REPLAY_TITLE();
    $[45] = LL;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t21) {
    t22 = <Typography variant="subtitle2" sx={t20}>{t21}</Typography>;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      color: "text.secondary"
    };
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== LL || $[51] !== activePlaybackStepIndex || $[52] !== callstackLength || $[53] !== playbackStepGroups.length) {
    t24 = LL.HOME_PREVIEW_STEP_PROGRESS({
      step: Math.max(activePlaybackStepIndex + 1, 0),
      total: playbackStepGroups.length || callstackLength
    });
    $[50] = LL;
    $[51] = activePlaybackStepIndex;
    $[52] = callstackLength;
    $[53] = playbackStepGroups.length;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== t24) {
    t25 = <Typography variant="caption" sx={t23}>{t24}</Typography>;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t22 || $[58] !== t25) {
    t26 = <Stack direction="row" sx={t19}>{t22}{t25}</Stack>;
    $[57] = t22;
    $[58] = t25;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== theme.appDesign.surfaceLowest) {
    t27 = alpha(theme.appDesign.surfaceLowest, 0.92);
    $[60] = theme.appDesign.surfaceLowest;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== theme.appDesign.outline) {
    t28 = alpha(theme.appDesign.outline, 0.12);
    $[62] = theme.appDesign.outline;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  const t29 = `1px solid ${t28}`;
  let t30;
  if ($[64] !== t27 || $[65] !== t29) {
    t30 = {
      position: "relative",
      minHeight: 212,
      borderRadius: 3,
      bgcolor: t27,
      border: t29,
      overflow: "hidden"
    };
    $[64] = t27;
    $[65] = t29;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== theme.appDesign.accent) {
    t31 = alpha(theme.appDesign.accent, 0.22);
    $[67] = theme.appDesign.accent;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  const t32 = `radial-gradient(circle at 50% 20%, ${t31} 0%, transparent 46%)`;
  let t33;
  if ($[69] !== t32) {
    t33 = <Box sx={{
      position: "absolute",
      inset: 0,
      background: t32,
      pointerEvents: "none"
    }} />;
    $[69] = t32;
    $[70] = t33;
  } else {
    t33 = $[70];
  }
  let t34;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Box sx={{
      position: "relative",
      zIndex: 1,
      height: "100%",
      px: 1,
      py: 1.25
    }}><TreeViewer playbackInterval={PLAYBACK_INTERVAL_MS} binaryTreeAlign="center" /></Box>;
    $[71] = t34;
  } else {
    t34 = $[71];
  }
  let t35;
  if ($[72] !== t30 || $[73] !== t33) {
    t35 = <Box sx={t30}>{t33}{t34}</Box>;
    $[72] = t30;
    $[73] = t33;
    $[74] = t35;
  } else {
    t35 = $[74];
  }
  let t36;
  if ($[75] !== theme.appDesign.surfaceLowest) {
    t36 = alpha(theme.appDesign.surfaceLowest, 0.86);
    $[75] = theme.appDesign.surfaceLowest;
    $[76] = t36;
  } else {
    t36 = $[76];
  }
  let t37;
  if ($[77] !== theme.appDesign.outline) {
    t37 = alpha(theme.appDesign.outline, 0.1);
    $[77] = theme.appDesign.outline;
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  const t38 = `1px solid ${t37}`;
  let t39;
  if ($[79] !== t36 || $[80] !== t38) {
    t39 = {
      borderRadius: 3,
      bgcolor: t36,
      border: t38,
      overflow: "hidden"
    };
    $[79] = t36;
    $[80] = t38;
    $[81] = t39;
  } else {
    t39 = $[81];
  }
  let t40;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = {
      alignItems: "center",
      px: 1.5,
      py: 1.25
    };
    $[82] = t40;
  } else {
    t40 = $[82];
  }
  let t41;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = {
      color: "text.secondary"
    };
    $[83] = t41;
  } else {
    t41 = $[83];
  }
  let t42;
  if ($[84] !== LL) {
    t42 = LL.CALLSTACK();
    $[84] = LL;
    $[85] = t42;
  } else {
    t42 = $[85];
  }
  let t43;
  if ($[86] !== t42) {
    t43 = <Stack direction="row" spacing={1} sx={t40}><Typography variant="subtitle2" sx={t41}>{t42}</Typography></Stack>;
    $[86] = t42;
    $[87] = t43;
  } else {
    t43 = $[87];
  }
  let t44;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <CompactCallstackList height={262} />;
    $[88] = t44;
  } else {
    t44 = $[88];
  }
  let t45;
  if ($[89] !== t39 || $[90] !== t43) {
    t45 = <Box sx={t39}>{t43}{t44}</Box>;
    $[89] = t39;
    $[90] = t43;
    $[91] = t45;
  } else {
    t45 = $[91];
  }
  let t46;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = {
      xs: "column",
      sm: "row"
    };
    $[92] = t46;
  } else {
    t46 = $[92];
  }
  let t47;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = {
      alignItems: "center",
      minWidth: 0
    };
    $[93] = t47;
  } else {
    t47 = $[93];
  }
  let t48;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = {
      justifyContent: {
        xs: "flex-end",
        sm: "flex-start"
      }
    };
    $[94] = t48;
  } else {
    t48 = $[94];
  }
  const t49 = !isReady || isRootFrame;
  let t50;
  if ($[95] !== LL) {
    t50 = LL.HOME_PREVIEW_STEP_BACK();
    $[95] = LL;
    $[96] = t50;
  } else {
    t50 = $[96];
  }
  let t51;
  if ($[97] !== LL) {
    t51 = LL.HOME_PREVIEW_STEP_BACK();
    $[97] = LL;
    $[98] = t51;
  } else {
    t51 = $[98];
  }
  let t52;
  if ($[99] !== theme.appDesign.surfaceHigh) {
    t52 = alpha(theme.appDesign.surfaceHigh, 0.9);
    $[99] = theme.appDesign.surfaceHigh;
    $[100] = t52;
  } else {
    t52 = $[100];
  }
  let t53;
  if ($[101] !== t52) {
    t53 = {
      bgcolor: t52
    };
    $[101] = t52;
    $[102] = t53;
  } else {
    t53 = $[102];
  }
  let t54;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <SkipPrevious fontSize="small" />;
    $[103] = t54;
  } else {
    t54 = $[103];
  }
  let t55;
  if ($[104] !== handleManualStepBack || $[105] !== t49 || $[106] !== t50 || $[107] !== t51 || $[108] !== t53) {
    t55 = <IconButton size="small" onClick={handleManualStepBack} disabled={t49} aria-label={t50} title={t51} sx={t53}>{t54}</IconButton>;
    $[104] = handleManualStepBack;
    $[105] = t49;
    $[106] = t50;
    $[107] = t51;
    $[108] = t53;
    $[109] = t55;
  } else {
    t55 = $[109];
  }
  const t56 = isLastFrame && !isPlaying ? handleManualReplay : handleManualPlayToggle;
  const t57 = !callstackLength;
  let t58;
  if ($[110] !== LL || $[111] !== isLastFrame || $[112] !== isPlaying) {
    t58 = isLastFrame && !isPlaying ? LL.REPLAY() : isPlaying ? LL.HOME_LANDING_PREVIEW_PAUSE() : LL.HOME_LANDING_PREVIEW_PLAY();
    $[110] = LL;
    $[111] = isLastFrame;
    $[112] = isPlaying;
    $[113] = t58;
  } else {
    t58 = $[113];
  }
  let t59;
  if ($[114] !== LL || $[115] !== isLastFrame || $[116] !== isPlaying) {
    t59 = isLastFrame && !isPlaying ? LL.REPLAY() : isPlaying ? LL.HOME_LANDING_PREVIEW_PAUSE() : LL.HOME_LANDING_PREVIEW_PLAY();
    $[114] = LL;
    $[115] = isLastFrame;
    $[116] = isPlaying;
    $[117] = t59;
  } else {
    t59 = $[117];
  }
  let t60;
  if ($[118] !== theme.appDesign.accent) {
    t60 = alpha(theme.appDesign.accent, 0.14);
    $[118] = theme.appDesign.accent;
    $[119] = t60;
  } else {
    t60 = $[119];
  }
  let t61;
  if ($[120] !== t60 || $[121] !== theme.appDesign.accentSoft) {
    t61 = {
      bgcolor: t60,
      color: theme.appDesign.accentSoft
    };
    $[120] = t60;
    $[121] = theme.appDesign.accentSoft;
    $[122] = t61;
  } else {
    t61 = $[122];
  }
  let t62;
  if ($[123] !== isLastFrame || $[124] !== isPlaying) {
    t62 = isLastFrame && !isPlaying ? <Replay fontSize="small" /> : isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />;
    $[123] = isLastFrame;
    $[124] = isPlaying;
    $[125] = t62;
  } else {
    t62 = $[125];
  }
  let t63;
  if ($[126] !== t56 || $[127] !== t57 || $[128] !== t58 || $[129] !== t59 || $[130] !== t61 || $[131] !== t62) {
    t63 = <IconButton size="small" onClick={t56} disabled={t57} aria-label={t58} title={t59} sx={t61}>{t62}</IconButton>;
    $[126] = t56;
    $[127] = t57;
    $[128] = t58;
    $[129] = t59;
    $[130] = t61;
    $[131] = t62;
    $[132] = t63;
  } else {
    t63 = $[132];
  }
  const t64 = !isReady || isLastFrame;
  let t65;
  if ($[133] !== LL) {
    t65 = LL.HOME_PREVIEW_STEP_FORWARD();
    $[133] = LL;
    $[134] = t65;
  } else {
    t65 = $[134];
  }
  let t66;
  if ($[135] !== LL) {
    t66 = LL.HOME_PREVIEW_STEP_FORWARD();
    $[135] = LL;
    $[136] = t66;
  } else {
    t66 = $[136];
  }
  let t67;
  if ($[137] !== theme.appDesign.surfaceHigh) {
    t67 = alpha(theme.appDesign.surfaceHigh, 0.9);
    $[137] = theme.appDesign.surfaceHigh;
    $[138] = t67;
  } else {
    t67 = $[138];
  }
  let t68;
  if ($[139] !== t67) {
    t68 = {
      bgcolor: t67
    };
    $[139] = t67;
    $[140] = t68;
  } else {
    t68 = $[140];
  }
  let t69;
  if ($[141] === Symbol.for("react.memo_cache_sentinel")) {
    t69 = <SkipNext fontSize="small" />;
    $[141] = t69;
  } else {
    t69 = $[141];
  }
  let t70;
  if ($[142] !== handleManualStepForward || $[143] !== t64 || $[144] !== t65 || $[145] !== t66 || $[146] !== t68) {
    t70 = <IconButton size="small" onClick={handleManualStepForward} disabled={t64} aria-label={t65} title={t66} sx={t68}>{t69}</IconButton>;
    $[142] = handleManualStepForward;
    $[143] = t64;
    $[144] = t65;
    $[145] = t66;
    $[146] = t68;
    $[147] = t70;
  } else {
    t70 = $[147];
  }
  let t71;
  if ($[148] !== t55 || $[149] !== t63 || $[150] !== t70) {
    t71 = <Stack direction="row" spacing={0.5} sx={t48}>{t55}{t63}{t70}</Stack>;
    $[148] = t55;
    $[149] = t63;
    $[150] = t70;
    $[151] = t71;
  } else {
    t71 = $[151];
  }
  let t72;
  if ($[152] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = {
      sm: 0.5
    };
    $[152] = t72;
  } else {
    t72 = $[152];
  }
  let t73;
  if ($[153] === Symbol.for("react.memo_cache_sentinel")) {
    t73 = {
      color: "text.secondary",
      ml: t72,
      lineHeight: 1.6,
      maxWidth: {
        xs: "100%",
        sm: 220
      },
      textAlign: {
        xs: "right",
        sm: "left"
      }
    };
    $[153] = t73;
  } else {
    t73 = $[153];
  }
  let t74;
  if ($[154] !== LL) {
    t74 = LL.HOME_PILLAR_VIS_BODY();
    $[154] = LL;
    $[155] = t74;
  } else {
    t74 = $[155];
  }
  let t75;
  if ($[156] !== t74) {
    t75 = <Typography variant="caption" sx={t73}>{t74}</Typography>;
    $[156] = t74;
    $[157] = t75;
  } else {
    t75 = $[157];
  }
  let t76;
  if ($[158] !== t71 || $[159] !== t75) {
    t76 = <Stack direction={t46} spacing={1} sx={t47}>{t71}{t75}</Stack>;
    $[158] = t71;
    $[159] = t75;
    $[160] = t76;
  } else {
    t76 = $[160];
  }
  let t77;
  if ($[161] !== t26 || $[162] !== t35 || $[163] !== t45 || $[164] !== t76) {
    t77 = <Stack ref={previewRootRef} spacing={1.5} sx={t18}>{t26}{t35}{t45}{t76}</Stack>;
    $[161] = t26;
    $[162] = t35;
    $[163] = t45;
    $[164] = t76;
    $[165] = t77;
  } else {
    t77 = $[165];
  }
  return t77;
};
export const HomeLandingHeroPreviewRuntime: React.FC<HomeLandingHeroPreviewRuntimeProps> = t0 => {
  const $ = _c(18);
  const {
    LL,
    pageScrollViewport
  } = t0;
  const [runtimeState] = useState(_temp);
  if (!runtimeState.store) {
    let t1;
    if ($[0] !== LL) {
      t1 = LL.HOME_LANDING_PREVIEW_LOAD_FAILED();
      $[0] = LL;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <Typography variant="subtitle2">{t1}</Typography>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== LL || $[5] !== runtimeState.errorMessage) {
      t3 = runtimeState.errorMessage || LL.HOME_LANDING_PREVIEW_ERROR_UNEXPECTED();
      $[4] = LL;
      $[5] = runtimeState.errorMessage;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== t3) {
      t4 = <Typography variant="caption">{t3}</Typography>;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== t2 || $[10] !== t4) {
      t5 = <Alert severity="error" variant="outlined">{t2}{t4}</Alert>;
      $[9] = t2;
      $[10] = t4;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    return t5;
  }
  let t1;
  if ($[12] !== LL || $[13] !== pageScrollViewport) {
    t1 = <HomeLandingHeroPreviewRuntimeInner LL={LL} pageScrollViewport={pageScrollViewport} />;
    $[12] = LL;
    $[13] = pageScrollViewport;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  let t2;
  if ($[15] !== runtimeState.store || $[16] !== t1) {
    t2 = <Provider store={runtimeState.store}>{t1}</Provider>;
    $[15] = runtimeState.store;
    $[16] = t1;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  return t2;
};
function _temp() {
  ;
  try {
    return {
      store: createLandingHeroPreviewStore(),
      errorMessage: null
    };
  } catch (t0) {
    const error = t0;
    return {
      store: null,
      errorMessage: getPreviewRuntimeErrorMessage(error)
    };
  }
}